import * as React from 'react'
import { InsightWithSource } from '@shared/utils/insights/types'
import { MeetingIcon } from '@renderer/chat'
import { Mic } from 'lucide-react'
import { useSetSelectedInsightId, useSetSidePanelOpen } from '@renderer/stores/side-panel'
import { useInsightActions } from '@renderer/hooks/useInsightActions'
import { appIconUrl } from '@renderer/utils/app-icon'

export const useInsightProperties = (insight?: InsightWithSource | null) => {
  const setSelectedInsightId = useSetSelectedInsightId()
  const setSidePanelOpen = useSetSidePanelOpen()
  const { deleteInsight } = useInsightActions()

  const icon = React.useMemo(() => {
    if (!insight) {
      return undefined
    }
    if (insight.source === 'audio-note') {
      if (insight.meetingNote?.meeting) {
        return <MeetingIcon appName={insight.meetingNote.meeting.app.name} size={24} />
      } else {
        return <Mic size={24} className="text-green" />
      }
    } else if (insight.screenSource?.appName) {
      return (
        <img
          src={appIconUrl(insight.screenSource.appName)}
          alt={insight.screenSource.appName}
          className="size-6 object-contain"
        />
      )
    }
    return undefined
  }, [insight])

  const hasBackgroundImage = React.useMemo(() => {
    if (!insight) {
      return false
    }
    return !!insight.screenSource?.base64DataUrl || !!insight.screenSource?.screenshotUuid
  }, [insight])

  const sourceLabel = React.useMemo(() => {
    if (!insight) {
      return ''
    }
    if (insight.source === 'audio-note') {
      const meetingNote = insight.meetingNote
      return `${
        meetingNote?.title ?? meetingNote?.meeting?.windowTitle ?? meetingNote?.meeting?.app?.name ?? 'Meeting Note'
      }`
    }
    const assignedBy = insight.assignedBy
    const appName = insight.screenSource?.appName
    if (assignedBy && appName) {
      return `${assignedBy} in ${appName}`
    }
    if (appName) {
      return `${appName}`
    }

    return ''
  }, [insight])

  const onDeleteInsight = React.useCallback(async () => {
    if (!insight) {
      return
    }
    return deleteInsight(insight.id)
  }, [deleteInsight, insight])

  const onOpenInsight = React.useCallback(() => {
    if (!insight) {
      return
    }
    setSelectedInsightId(insight.id)
    setSidePanelOpen(true)
  }, [setSelectedInsightId, setSidePanelOpen, insight])

  return React.useMemo(() => {
    return {
      icon,
      sourceLabel,
      hasBackgroundImage,
      onOpenInsight,
      onDeleteInsight,
    }
  }, [icon, sourceLabel, hasBackgroundImage, onOpenInsight, onDeleteInsight])
}
